import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Exploring the AST with Babylon and Prettier",
  "description": "",
  "published": "2017-06-08T00:00:00.000Z",
  "redirect_from": ["/blog/exploring-ast-babylon-prettier/swizec/7633"],
  "categories": "Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "zTE2QjKBJ-E",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`At `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/livecoding-39-towards-ai-writes-javascript/swizec/7621"
      }}>{`this week's livecoding session`}</a>{`, I mentioned mutating ASTs to improve the performance of our `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/faux-js-ai"
      }}>{`faux AI that writes JavaScript`}</a>{` algorithm. It would help us converge on a solution faster.`}</p>
    <p>{`But what is an AST?`}</p>
    <p>{`Well, it's an abstract syntax tree. It's that thing computers use to understand your code. Let me explain how they work. I think every programmer can benefit from knowing this stuff.`}</p>
    <p>{`It took my compilers professor in college a few hours. Let's see if I can show you the gist of it in a few hundred words. With a picture or two.`}</p>
    <p>{`We're going to use `}<a parentName="p" {...{
        "href": "https://github.com/babel/babylon"
      }}>{`Babylon`}</a>{` to parse code into an AST and `}<a parentName="p" {...{
        "href": "https://github.com/prettier/prettier"
      }}>{`Prettier`}</a>{` to print it back out into code. Coincidentally, that's how Prettier approaches making your code pretty. You'll see that too.`}</p>
    <p>{`We begin with a test harness runnable in node.js.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const util = require("util");
const babylon = require("babylon");

const code = \`
\`;

let AST = babylon.parse(code);

console.log(util.inspect(AST, false, null));
`}</code></pre>
    <p>{`We're going to put the code we're testing into `}<inlineCode parentName="p">{`code`}</inlineCode>{`. The rest is there to parse it and print the resulting AST. `}<inlineCode parentName="p">{`babylon.parse`}</inlineCode>{` is where the magic happens.`}</p>
    <p>{`Parsing an empty piece of code creates an AST like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`Node {
  type: 'File',
  start: 0,
  end: 1,
  loc:
   SourceLocation {
     start: Position { line: 1, column: 0 },
     end: Position { line: 2, column: 0 } },
  program:
   Node {
     type: 'Program',
     start: 0,
     end: 1,
     loc:
      SourceLocation {
        start: Position { line: 1, column: 0 },
        end: Position { line: 2, column: 0 } },
     sourceType: 'script',
     body: [],
     directives: [] },
  comments: [],
  tokens:
   [ Token {
       type:
        TokenType {
          label: 'eof',
          keyword: undefined,
          beforeExpr: false,
          startsExpr: false,
          rightAssociative: false,
          isLoop: false,
          isAssign: false,
          prefix: false,
          postfix: false,
          binop: null,
          updateContext: null },
       value: undefined,
       start: 1,
       end: 1,
       loc:
        SourceLocation {
          start: Position { line: 2, column: 0 },
          end: Position { line: 2, column: 0 } } } ] }
`}</code></pre>
    <p>{`That doesn't tell you much, does it? It doesn't tell me anything. Most of it is for Babylon to know what's going on.`}</p>
    <p>{`Here's the part we care about:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`  program:
   Node {
     type: 'Program',
     start: 0,
     end: 1,
     loc:
      SourceLocation {
        start: Position { line: 1, column: 0 },
        end: Position { line: 2, column: 0 } },
     sourceType: 'script',
     body: [],
     directives: [] },
`}</code></pre>
    <p>{`You can read this as `}<em parentName="p">{`"There is a program that starts at 0:1 and ends at 0:2 and has an empty body. It is a script."`}</em>{`. That means our code isn't as empty as we thought; it's got a whole empty line!`}</p>
    <p>{`In a picture, that AST looks something like this:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/06/fileprogram1.png",
        "alt": null
      }}></img></p>
    <p>{`Root `}<inlineCode parentName="p">{`file`}</inlineCode>{` node, child `}<inlineCode parentName="p">{`program`}</inlineCode>{` node.`}</p>
    <p>{`Now let's try a variable assignment.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const code = \`
let a = 5;
\`;
`}</code></pre>
    <p>{`That outputs a bigger AST. I'm going to snip out some of the details to make this readable.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`program:
   Node {
     type: 'Program',
     start: 0,
     end: 12,
     loc:
      SourceLocation {},
     sourceType: 'script',
     body:
      [ Node {
          type: 'VariableDeclaration',
          start: 1,
          end: 11,
          loc:
           SourceLocation {},
          declarations:
           [ Node {
               type: 'VariableDeclarator',
               start: 5,
               end: 10,
               loc:
                SourceLocation {},
               id:
                Node {
                  type: 'Identifier',
                  start: 5,
                  end: 6,
                  loc:
                   SourceLocation {},
                  name: 'a' },
               init:
                Node {
                  type: 'NumericLiteral',
                  start: 9,
                  end: 10,
                  loc:
                   SourceLocation {},
                  extra: { rawValue: 5, raw: '5' },
                  value: 5 } } ],
          kind: 'let' } ],
     directives: [] },
`}</code></pre>
    <p>{`Our body now has a `}<inlineCode parentName="p">{`variableDeclaration`}</inlineCode>{` node, which contains a `}<inlineCode parentName="p">{`variableDeclarator`}</inlineCode>{` with an `}<inlineCode parentName="p">{`Identifier`}</inlineCode>{` and a `}<inlineCode parentName="p">{`NumericLiteral`}</inlineCode>{`. From this AST, you can infer that a `}<inlineCode parentName="p">{`variableDeclarator`}</inlineCode>{` can contain multiple declarators, and that each declarator has an `}<inlineCode parentName="p">{`id`}</inlineCode>{` and an `}<inlineCode parentName="p">{`init`}</inlineCode>{`. The `}<inlineCode parentName="p">{`id`}</inlineCode>{` node identifies the resulting variable and `}<inlineCode parentName="p">{`init`}</inlineCode>{` sets its value.`}</p>
    <p>{`In a picture, that looks something like this:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/06/image2.png",
        "alt": null
      }}></img></p>
    <p>{`You'll notice the AST doesn't talk much about what our code says. There's no lets and equal signs or any of that. Sure, the identifier has a `}<inlineCode parentName="p">{`name`}</inlineCode>{` and the init has a `}<inlineCode parentName="p">{`value`}</inlineCode>{`, but the rest is an abstraction level higher.`}</p>
    <p>{`An AST talks about what the code does, not what it is.`}</p>
    <p>{`There's a list of tokens if you want to know what the code says:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`tokens:
   [ Token {
       type:
        KeywordTokenType {
          label: 'let',
          keyword: 'let',
          beforeExpr: false,
          startsExpr: false,
          rightAssociative: false,
          isLoop: false,
          isAssign: false,
          prefix: false,
          postfix: false,
          binop: null,
          updateContext: null },
       value: 'let',
       start: 1,
       end: 4,
       loc:
        SourceLocation {},
     Token {
       type:
        TokenType {
          label: 'name',
          keyword: undefined,
          beforeExpr: false,
          startsExpr: true,
          rightAssociative: false,
          isLoop: false,
          isAssign: false,
          prefix: false,
          postfix: false,
          binop: null,
          updateContext: [Function] },
       value: 'a',
       start: 5,
       end: 6,
       loc:
        SourceLocation {},
     Token {
       type:
        TokenType {
          label: '=',
          keyword: undefined,
          beforeExpr: true,
          startsExpr: false,
          rightAssociative: false,
          isLoop: false,
          isAssign: true,
          prefix: false,
          postfix: false,
          binop: null,
          updateContext: null },
       value: '=',
       start: 7,
       end: 8,
       loc:
        SourceLocation { },
     Token {
       type:
        TokenType {
          label: 'num',
          keyword: undefined,
          beforeExpr: false,
          startsExpr: true,
          rightAssociative: false,
          isLoop: false,
          isAssign: false,
          prefix: false,
          postfix: false,
          binop: null,
          updateContext: null },
       value: 5,
       start: 9,
       end: 10,
       loc:
        SourceLocation {},
     Token {
       type:
        TokenType {
          label: ';',
          keyword: undefined,
          beforeExpr: true,
          startsExpr: false,
          rightAssociative: false,
          isLoop: false,
          isAssign: false,
          prefix: false,
          postfix: false,
          binop: null,
          updateContext: null },
       value: undefined,
       start: 10,
       end: 11,
       loc:
        SourceLocation {},
     Token {
       type:
        TokenType {
          label: 'eof',
          keyword: undefined,
          beforeExpr: false,
          startsExpr: false,
          rightAssociative: false,
          isLoop: false,
          isAssign: false,
          prefix: false,
          postfix: false,
          binop: null,
          updateContext: null },
       value: undefined,
       start: 12,
       end: 12,
       loc:
        SourceLocation { } ]
`}</code></pre>
    <p>{`The list of tokens is a lot more interesting to Babylon than it is to you or I. It explains how to translate your code into a syntax tree.`}</p>
    <p>{`For example, let's say our parser encounters the `}<inlineCode parentName="p">{`a`}</inlineCode>{` token. Just like you, it recognizes that this is not a JavaScript reserved word. That must mean it's a name for something and it starts a new expression.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`Token {
   type:
    TokenType {
      label: 'name',
      keyword: undefined,
      beforeExpr: false,
      startsExpr: true,
      rightAssociative: false,
      isLoop: false,
      isAssign: false,
      prefix: false,
      postfix: false,
      binop: null,
      updateContext: [Function] },
   value: 'a',
   start: 5,
   end: 6
}
`}</code></pre>
    <p>{`The parser keeps going. It doesn't know yet what this new expression is going to be, but its name is `}<inlineCode parentName="p">{`a`}</inlineCode>{`.`}</p>
    <p>{`So it finds the `}<inlineCode parentName="p">{`=`}</inlineCode>{` sign.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`type:
TokenType {
  label: '=',
  keyword: undefined,
  beforeExpr: true,
  startsExpr: false,
  rightAssociative: false,
  isLoop: false,
  isAssign: true,
  prefix: false,
  postfix: false,
  binop: null,
  updateContext: null },
value: '=',
`}</code></pre>
    <p>{`Ah, so we're doing assignment, `}<inlineCode parentName="p">{`isAssign`}</inlineCode>{`, and we're expecting an expression afterwards. At least that's what I think `}<inlineCode parentName="p">{`beforeExpr`}</inlineCode>{` means. It's hard to know for sure because `}<inlineCode parentName="p">{`;`}</inlineCode>{` also gets the `}<inlineCode parentName="p">{`beforeExpr`}</inlineCode>{` flag 🤔`}</p>
    <p>{`Either way, Babylon then encounters a `}<inlineCode parentName="p">{`num`}</inlineCode>{` token, which also starts a new expression. In this case, an expression that's the length of a single token, but still a new expression.`}</p>
    <p>{`As the parser goes through these tokens, it builds a syntax tree. Some tokens build new branches; others add more children to existing nodes.`}</p>
    <p>{`Making this work is tricky. I remember building a compiler at school, and it nearly broke my brain. The hard part is that you're reading a flat stream of tokens and converting it into a rich tree.`}</p>
    <p>{`And yet, you do it every day when reading other people's code! Quite impressive.`}</p>
    <p>{`Now, once you have an AST, you can use something like Prettier to print it back out into readable code.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const code = \`
let

a
         =

 5;
\`;

console.log(prettier.format(code));

// let a = 5;
`}</code></pre>
    <p>{`I couldn't find the internal Prettier function that takes an AST and outputs it, but that's what `}<inlineCode parentName="p">{`.format`}</inlineCode>{` does internally. Parses your code, prints it back out with less cruft.`}</p>
    <p>{`When I have some time, I'll figure out how to manipulate those ASTs to create different code.`}</p>
    <p>{`Now excuse me while I install Prettier in my editor. 🤓`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      